import React, { useCallback, useEffect, useState } from 'react';
import Layout from '../../components/layout/Layout';
import RichTextEditor from '../../components/RichTextEditor/RichTextEditor';
import { getData, postData, putData, deleteData } from '../../services/apiService';
import { showConfirmation, showSuccess, showError } from '../../hooks/swalAlerts';
import useDebounce from '../../hooks/useDebounce';
import Progress from '../../hooks/useProgressBar';
import { Backdrop } from "@mui/material";
import {
    Box, Typography, Tabs, Tab, Paper, Button, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, IconButton, Modal, TextField, Select,
    MenuItem, FormControl, InputLabel, Pagination, Chip, Grid, Switch,
    FormControlLabel, Checkbox, ListItemText, OutlinedInput, Alert,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import CampaignIcon from '@mui/icons-material/Campaign';
import RuleIcon from '@mui/icons-material/Rule';
import HistoryIcon from '@mui/icons-material/History';
import EmailIcon from '@mui/icons-material/Email';
import ArticleIcon from '@mui/icons-material/Article';

const API = '/admin/notifications';
const PRIMARY = '#4B49AC';

const TAB_LABELS = ['Overview', 'Templates', 'Campaigns', 'Automated Rules', 'Campaign History', 'Delivery Logs'];

const RULE_TYPE_LABELS = {
    not_started_3_days: 'Not Started After 3 Days',
    not_started_7_days: 'Not Started After 7 Days',
    inactive_7_days: 'Inactive For 7 Days',
    inactive_15_days: 'Inactive For 15 Days',
    assessment_not_attempted: 'Assessment Not Attempted',
    assessment_failed: 'Assessment Failed',
    assessment_passed: 'Assessment Passed',
    // exam_eligible: 'Exam Eligible',
    // exam_registered: 'Exam Registered',
    // exam_not_registered: 'Exam Not Registered',
    // exam_passed: 'Exam Passed',
    // exam_failed: 'Exam Failed',
    // custom: 'Custom',
};

const defaultSchedule = () => ({
    frequency: 'daily',
    day_of_week: 1,
    day_of_month: 1,
    hour: 9,
    minute: 0,
});

const formatScheduleLabel = (schedule = {}) => {
    const s = { ...defaultSchedule(), ...schedule };
    const time = `${String(s.hour).padStart(2, '0')}:${String(s.minute).padStart(2, '0')}`;
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    if (s.frequency === 'weekly') return `Weekly on ${days[s.day_of_week]} at ${time}`;
    if (s.frequency === 'monthly') return `Monthly on day ${s.day_of_month} at ${time}`;
    return `Daily at ${time}`;
};

const defaultFilters = () => ({
    institute: { mode: 'all', ids: [] },
    course: { mode: 'all', ids: [] },
    progress: { type: '', value: '' },
    activity: { inactiveDays: '' },
    students: { mode: 'all', ids: [] },
});

const NotificationCenter = () => {
    const [tab, setTab] = useState(0);
    const [loading, setLoading] = useState(false);
    const [campaignLoading, setCampaignLoading] = useState(false);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [stats, setStats] = useState({});
    const [placeholders, setPlaceholders] = useState([]);

    // Templates
    const [templates, setTemplates] = useState([]);
    const [templatePage, setTemplatePage] = useState(1);
    const [templateTotalPages, setTemplateTotalPages] = useState(1);
    const [templateSearch, setTemplateSearch] = useState('');
    const debouncedTemplateSearch = useDebounce(templateSearch, 500);
    const [templateModal, setTemplateModal] = useState(false);
    const [templateForm, setTemplateForm] = useState({ name: '', subject: '', body: '', type: 'general', status: 'active' });
    const [editingTemplate, setEditingTemplate] = useState(null);

    // Campaigns
    const [campaigns, setCampaigns] = useState([]);
    const [campaignPage, setCampaignPage] = useState(1);
    const [campaignTotalPages, setCampaignTotalPages] = useState(1);
    const [campaignModal, setCampaignModal] = useState(false);
    const [editingCampaign, setEditingCampaign] = useState(null);
    const [campaignForm, setCampaignForm] = useState({
        title: '', subject: '', body: '', template_id: '', send_mode: 'now', scheduled_at: '', filters_json: defaultFilters(),
    });
    const [recipientPreview, setRecipientPreview] = useState(null);
    const [colleges, setColleges] = useState([]);
    const [courses, setCourses] = useState([]);
    const [templateDropdown, setTemplateDropdown] = useState([]);

    // Rules
    const [rules, setRules] = useState([]);
    const [rulePage, setRulePage] = useState(1);
    const [ruleTotalPages, setRuleTotalPages] = useState(1);
    const [ruleTypes, setRuleTypes] = useState([]);
    const [scheduleOptions, setScheduleOptions] = useState({ days_of_week: [] });
    const [ruleModal, setRuleModal] = useState(false);
    const [ruleForm, setRuleForm] = useState({
        name: '', rule_type: '', template_id: '', status: 'active', allow_resend: false,
        conditions_json: { passThreshold: 60 }, schedule: defaultSchedule(),
    });
    const [editingRule, setEditingRule] = useState(null);

    // History & Logs
    const [history, setHistory] = useState([]);
    const [historyPage, setHistoryPage] = useState(1);
    const [historyTotalPages, setHistoryTotalPages] = useState(1);
    const [logs, setLogs] = useState([]);
    const [logPage, setLogPage] = useState(1);
    const [logTotalPages, setLogTotalPages] = useState(1);
    const [logStatus, setLogStatus] = useState('');

    const fetchStats = useCallback(async () => {
        try {
            const data = await getData(`${API}/stats`);
            setStats(data || {});
        } catch (e) { console.error(e); }
    }, []);

    const fetchPlaceholders = useCallback(async () => {
        try {
            const data = await getData(`${API}/placeholders`);
            setPlaceholders(data?.placeholders || []);
        } catch (e) { console.error(e); }
    }, []);

    const fetchTemplates = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getData(`${API}/templates?page=${templatePage}&limit=10&search=${debouncedTemplateSearch}`);
            setTemplates(res?.data || []);
            setTemplateTotalPages(res?.totalPages || 1);
        } catch (e) { console.error(e); }
        setLoading(false);
    }, [templatePage, debouncedTemplateSearch]);

    const fetchCampaigns = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getData(`${API}/campaigns?page=${campaignPage}&limit=10`);
            setCampaigns(res?.data || []);
            setCampaignTotalPages(res?.totalPages || 1);
        } catch (e) { console.error(e); }
        setLoading(false);
    }, [campaignPage]);

    const fetchRules = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getData(`${API}/rules?page=${rulePage}&limit=10`);
            setRules(res?.data || []);
            setRuleTotalPages(res?.totalPages || 1);
        } catch (e) { console.error(e); }
        setLoading(false);
    }, [rulePage]);

    const fetchHistory = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getData(`${API}/campaigns/history?page=${historyPage}&limit=10`);
            setHistory(res?.data || []);
            setHistoryTotalPages(res?.totalPages || 1);
        } catch (e) { console.error(e); }
        setLoading(false);
    }, [historyPage]);

    const fetchLogs = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getData(`${API}/logs?page=${logPage}&limit=10&status=${logStatus}`);
            setLogs(res?.data || []);
            setLogTotalPages(res?.totalPages || 1);
        } catch (e) { console.error(e); }
        setLoading(false);
    }, [logPage, logStatus]);

    const fetchDropdowns = useCallback(async () => {
        try {
            const [collegeRes, courseRes, templateRes, ruleTypeRes] = await Promise.all([
                getData('/get-colleges-for-dropdown'),
                getData('/get-courses-for-dropdown'),
                getData(`${API}/templates/dropdown`),
                getData(`${API}/rule-types`),
            ]);
            setColleges(collegeRes || []);
            setCourses(courseRes || []);
            setTemplateDropdown(templateRes || []);
            setRuleTypes(ruleTypeRes?.rule_types || []);
            setScheduleOptions(ruleTypeRes?.schedule_options || { days_of_week: [] });
        } catch (e) { console.error(e); }
    }, []);

    useEffect(() => {
        fetchStats();
        fetchPlaceholders();
        fetchDropdowns();
    }, [fetchStats, fetchPlaceholders, fetchDropdowns]);

    useEffect(() => {
        if (tab === 1) fetchTemplates();
        if (tab === 2) fetchCampaigns();
        if (tab === 3) fetchRules();
        if (tab === 4) fetchHistory();
        if (tab === 5) fetchLogs();
    }, [tab, fetchTemplates, fetchCampaigns, fetchRules, fetchHistory, fetchLogs]);

    const handleSaveTemplate = async () => {
        try {
            if (editingTemplate) {
                await putData(`${API}/templates/${editingTemplate._id}`, templateForm);
                showSuccess('Template updated');
            } else {
                await postData(`${API}/templates`, templateForm);
                showSuccess('Template created');
            }
            setTemplateModal(false);
            setEditingTemplate(null);
            setTemplateForm({ name: '', subject: '', body: '', type: 'general', status: 'active' });
            fetchTemplates();
            fetchDropdowns();
        } catch (e) {
            showError(e.response?.data?.message || 'Failed to save template');
        }
    };

    const handleDeleteTemplate = async (id) => {
        if (!await showConfirmation('Delete template?', 'This cannot be undone.')) return;
        try {
            await deleteData(`${API}/templates/${id}`);
            showSuccess('Template deleted');
            fetchTemplates();
        } catch (e) {
            showError(e.response?.data?.message || 'Failed to delete');
        }
    };

    const applyTemplateToCampaign = (templateId) => {
        const t = templateDropdown.find((x) => x._id === templateId);
        if (t) {
            setCampaignForm((prev) => ({
                ...prev,
                template_id: templateId,
                subject: t.subject || prev.subject,
            }));
        }
    };

    const loadFullTemplate = async (templateId) => {
        if (!templateId) return;
        try {
            const t = await getData(`${API}/templates/${templateId}`);
            setCampaignForm((prev) => ({
                ...prev,
                template_id: templateId,
                subject: t.subject || prev.subject,
                body: t.body || prev.body,
            }));
        } catch (e) { console.error(e); }
    };

    // const previewRecipients = async () => {
    //     try {
    //         const res = await postData(`${API}/campaigns/preview-recipients`, {
    //             filters_json: campaignForm.filters_json,
    //         });
    //         setRecipientPreview(res.recipient_count);
    //     } catch (e) {
    //         showError('Failed to preview recipients');
    //     }
    // };

    const previewRecipients = async () => {
        setPreviewLoading(true);

        try {
            const res = await postData( `${API}/campaigns/preview-recipients`, {
                    filters_json: campaignForm.filters_json,
                });
            setRecipientPreview(res.recipient_count);
        } catch (e) {
            showError('Failed to preview recipients');
        } finally {
            setPreviewLoading(false);
        }
    };

    const resetCampaignForm = () => {
        setEditingCampaign(null);
        setCampaignForm({ title: '', subject: '', body: '', template_id: '', send_mode: 'now', scheduled_at: '', filters_json: defaultFilters() });
        setRecipientPreview(null);
    };

    const formatDateTimeLocal = (date) => {
        if (!date) return '';
        const d = new Date(date);
        const pad = (n) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };

    const mergeFilters = (filters = {}) => ({
        ...defaultFilters(),
        ...filters,
        institute: { ...defaultFilters().institute, ...(filters.institute || {}) },
        course: { ...defaultFilters().course, ...(filters.course || {}) },
        progress: { ...defaultFilters().progress, ...(filters.progress || {}) },
        activity: { ...defaultFilters().activity, ...(filters.activity || {}) },
        students: { ...defaultFilters().students, ...(filters.students || {}) },
    });

    const openCreateCampaignModal = () => {
        resetCampaignForm();
        setCampaignModal(true);
    };

    const handleEditCampaign = async (id) => {
        try {
            const campaign = await getData(`${API}/campaigns/${id}`);
            setEditingCampaign(campaign);
            setCampaignForm({
                title: campaign.title || '',
                subject: campaign.subject || '',
                body: campaign.body || '',
                template_id: campaign.template_id?._id || campaign.template_id || '',
                send_mode: campaign.status === 'scheduled' ? 'schedule' : 'draft',
                scheduled_at: formatDateTimeLocal(campaign.scheduled_at),
                filters_json: mergeFilters(campaign.filters_json),
            });
            setRecipientPreview(null);
            setCampaignModal(true);
        } catch (e) {
            showError(e.response?.data?.message || 'Failed to load campaign');
        }
    };

    const handleSaveCampaign = async () => {
        setCampaignLoading(true);

        try {
            const payload = {
                ...campaignForm,
                template_id: campaignForm.template_id || undefined,
                scheduled_at:
                    campaignForm.send_mode === 'schedule'
                        ? campaignForm.scheduled_at
                        : undefined,
            };

            if (editingCampaign) {
                const res = await putData(
                    `${API}/campaigns/${editingCampaign._id}`,
                    payload
                );
                showSuccess(res.message || 'Campaign updated');
            } else {
                const res = await postData(
                    `${API}/campaigns`,
                    payload
                );
                showSuccess(res.message || 'Campaign created');
            }

            setCampaignModal(false);
            resetCampaignForm();
            fetchCampaigns();
            fetchStats();
        } catch (e) {
            showError(
                e.response?.data?.message ||
                `Failed to ${editingCampaign ? 'update' : 'create'} campaign`
            );
        } finally {
            setCampaignLoading(false);
        }
    };

    // const handleSaveCampaign = async () => {
    //     try {
    //         const payload = {
    //             ...campaignForm,
    //             template_id: campaignForm.template_id || undefined,
    //             scheduled_at: campaignForm.send_mode === 'schedule' ? campaignForm.scheduled_at : undefined,
    //         };

    //         if (editingCampaign) {
    //             const res = await putData(`${API}/campaigns/${editingCampaign._id}`, payload);
    //             showSuccess(res.message || 'Campaign updated');
    //         } else {
    //             const res = await postData(`${API}/campaigns`, payload);
    //             showSuccess(res.message || 'Campaign created');
    //         }

    //         setCampaignModal(false);
    //         resetCampaignForm();
    //         fetchCampaigns();
    //         fetchStats();
    //     } catch (e) {
    //         showError(e.response?.data?.message || `Failed to ${editingCampaign ? 'update' : 'create'} campaign`);
    //     }
    // };

    const handleDeleteCampaign = async (campaign) => {
        const isSent = campaign.status === 'sent';
        const confirmed = await showConfirmation(
            'Delete campaign?',
            isSent
                ? 'This campaign has already been sent. Delivery logs will be kept, but the campaign record will be removed.'
                : 'This cannot be undone.'
        );
        if (!confirmed) return;

        try {
            await deleteData(`${API}/campaigns/${campaign._id}`);
            showSuccess('Campaign deleted');
            fetchCampaigns();
            fetchStats();
        } catch (e) {
            showError(e.response?.data?.message || 'Failed to delete campaign');
        }
    };

    const handleSendCampaign = async (id) => {
        if (!await showConfirmation('Send campaign now?', 'Emails will be queued for delivery.')) return;
        try {
            const res = await postData(`${API}/campaigns/${id}/send`);
            showSuccess(`Queued ${res.queued || 0} emails to ${res.recipient_count || 0} recipients`);
            fetchCampaigns();
        } catch (e) {
            showError(e.response?.data?.message || 'Failed to send');
        }
    };

    const handleSaveRule = async () => {
        try {
            if (editingRule) {
                await putData(`${API}/rules/${editingRule._id}`, ruleForm);
                showSuccess('Rule updated');
            } else {
                await postData(`${API}/rules`, ruleForm);
                showSuccess('Rule created');
            }
            setRuleModal(false);
            setEditingRule(null);
            setRuleForm({
                name: '', rule_type: '', template_id: '', status: 'active', allow_resend: false,
                conditions_json: { passThreshold: 60 }, schedule: defaultSchedule(),
            });
            fetchRules();
            fetchStats();
        } catch (e) {
            showError(e.response?.data?.message || 'Failed to save rule');
        }
    };

    const handleDeleteRule = async (id) => {
        if (!await showConfirmation('Delete rule?', 'Automated notifications for this rule will stop.')) return;
        try {
            await deleteData(`${API}/rules/${id}`);
            showSuccess('Rule deleted');
            fetchRules();
        } catch (e) {
            showError(e.response?.data?.message || 'Failed to delete');
        }
    };

    const updateFilter = (section, field, value) => {
        setCampaignForm((prev) => ({
            ...prev,
            filters_json: {
                ...prev.filters_json,
                [section]: { ...prev.filters_json[section], [field]: value },
            },
        }));
        setRecipientPreview(null);
    };

    const StatCard = ({ title, value, icon }) => (
        <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Box sx={{ color: PRIMARY, mb: 1 }}>{icon}</Box>
            <Typography variant="h5" fontWeight={700}>{value ?? 0}</Typography>
            <Typography variant="body2" color="text.secondary">{title}</Typography>
        </Paper>
    );

    const statusChip = (status) => {
        const colors = { active: 'success', inactive: 'default', sent: 'success', failed: 'error', scheduled: 'info', processing: 'warning', draft: 'default', queued: 'info' };
        return <Chip size="small" label={status} color={colors[status] || 'default'} />;
    };

    const modalBox = { position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: { xs: '95vw', sm: 700, md: 900 }, maxHeight: '90vh', overflow: 'auto', bgcolor: 'background.paper', boxShadow: 24, p: 3, borderRadius: 2 };

    return (
        <Layout>
            {loading && <Progress />}
            {/* {(loading || campaignLoading || previewLoading) && <Progress />} */}
            <Box className="p-4 sm:p-6 bg-gray-100 min-h-screen">
                <Typography variant="h5" fontWeight={700} sx={{ color: PRIMARY, mb: 1 }}>
                    Notification & Communication Center
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Manage email templates, manual campaigns, automated rules, and delivery logs.
                </Typography>

                <Paper sx={{ mb: 3 }}>
                    <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto"
                        sx={{ '& .MuiTab-root.Mui-selected': { color: PRIMARY }, '& .MuiTabs-indicator': { bgcolor: PRIMARY } }}>
                        {TAB_LABELS.map((label) => <Tab key={label} label={label} />)}
                    </Tabs>
                </Paper>

                {/* Overview */}
                {tab === 0 && (
                    <Grid container spacing={2}>
                        <Grid sx={{ xs: 6, md: 2.4 }}><StatCard title="Active Templates" value={stats.totalTemplates} icon={<ArticleIcon />} /></Grid>
                        <Grid sx={{ xs: 6, md: 2.4 }}><StatCard title="Active Rules" value={stats.activeRules} icon={<RuleIcon />} /></Grid>
                        <Grid sx={{ xs: 6, md: 2.4 }}><StatCard title="Scheduled Campaigns" value={stats.scheduledCampaigns} icon={<CampaignIcon />} /></Grid>
                        <Grid sx={{ xs: 6, md: 2.4 }}><StatCard title="Sent Today" value={stats.sentToday} icon={<EmailIcon />} /></Grid>
                        <Grid sx={{ xs: 6, md: 2.4 }}><StatCard title="Failed Today" value={stats.failedToday} icon={<HistoryIcon />} /></Grid>
                        <Grid sx={{ xs: 12 }}>
                            <Alert severity="info" sx={{ mt: 2 }}>
                                Dynamic placeholders: {placeholders.map((p) => `{{${p}}}`).join(', ')}
                            </Alert>
                        </Grid>
                    </Grid>
                )}

                {/* Templates */}
                {tab === 1 && (
                    <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, gap: 2, flexWrap: 'wrap' }}>
                            <TextField size="small" placeholder="Search templates..." value={templateSearch} onChange={(e) => { setTemplateSearch(e.target.value); setTemplatePage(1); }} />
                            <Button variant="contained" sx={{ bgcolor: PRIMARY }} onClick={() => { setEditingTemplate(null); setTemplateForm({ name: '', subject: '', body: '', type: 'general', status: 'active' }); setTemplateModal(true); }}>
                                Create Template
                            </Button>
                        </Box>
                        <TableContainer component={Paper}>
                            <Table size="small">
                                <TableHead sx={{ bgcolor: PRIMARY }}>
                                    <TableRow>
                                        {['Name', 'Subject', 'Type', 'Status', 'Actions'].map((h) => (
                                            <TableCell key={h} sx={{ color: '#fff', fontWeight: 600 }}>{h}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {templates.map((t) => (
                                        <TableRow key={t._id} hover>
                                            <TableCell>{t.name}</TableCell>
                                            <TableCell>{t.subject}</TableCell>
                                            <TableCell>{t.type}</TableCell>
                                            <TableCell>{statusChip(t.status)}</TableCell>
                                            <TableCell>
                                                <IconButton size="small" onClick={() => { setEditingTemplate(t); setTemplateForm({ name: t.name, subject: t.subject, body: t.body, type: t.type, status: t.status }); setTemplateModal(true); }}><EditIcon fontSize="small" /></IconButton>
                                                <IconButton size="small" color="error" onClick={() => handleDeleteTemplate(t._id)}><DeleteIcon fontSize="small" /></IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <Pagination count={templateTotalPages} page={templatePage} onChange={(_, p) => setTemplatePage(p)} sx={{ mt: 2, '& .Mui-selected': { bgcolor: `${PRIMARY} !important` } }} />
                    </Box>
                )}

                {/* Campaigns */}
                {tab === 2 && (
                    <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                            <Button variant="contained" sx={{ bgcolor: PRIMARY }} onClick={openCreateCampaignModal}>Create Campaign</Button>
                        </Box>
                        <TableContainer component={Paper}>
                            <Table size="small">
                                <TableHead sx={{ bgcolor: PRIMARY }}>
                                    <TableRow>
                                        {['Title', 'Subject', 'Recipients', 'Status', 'Scheduled', 'Actions'].map((h) => (
                                            <TableCell key={h} sx={{ color: '#fff', fontWeight: 600 }}>{h}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {campaigns.map((c) => (
                                        <TableRow key={c._id} hover>
                                            <TableCell>{c.title}</TableCell>
                                            <TableCell>{c.subject}</TableCell>
                                            <TableCell>{c.recipient_count} (sent: {c.sent_count}, failed: {c.failed_count})</TableCell>
                                            <TableCell>{statusChip(c.status)}</TableCell>
                                            <TableCell>{c.scheduled_at ? new Date(c.scheduled_at).toLocaleString() : '-'}</TableCell>
                                            <TableCell>
                                                {['draft', 'scheduled', 'failed'].includes(c.status) && (
                                                    <>
                                                        <IconButton size="small" title="Edit" onClick={() => handleEditCampaign(c._id)}><EditIcon fontSize="small" /></IconButton>
                                                        <IconButton size="small" color="primary" title="Send now" onClick={() => handleSendCampaign(c._id)}><SendIcon fontSize="small" /></IconButton>
                                                    </>
                                                )}
                                                {c.status !== 'processing' && (
                                                    <IconButton size="small" color="error" title="Delete" onClick={() => handleDeleteCampaign(c)}><DeleteIcon fontSize="small" /></IconButton>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <Pagination count={campaignTotalPages} page={campaignPage} onChange={(_, p) => setCampaignPage(p)} sx={{ mt: 2 }} />
                    </Box>
                )}

                {/* Rules */}
                {tab === 3 && (
                    <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                            <Button variant="contained" sx={{ bgcolor: PRIMARY }} onClick={() => { setEditingRule(null); setRuleForm({ name: '', rule_type: '', template_id: '', status: 'active', allow_resend: false, conditions_json: { passThreshold: 60 }, schedule: defaultSchedule() }); setRuleModal(true); }}>
                                Create Rule
                            </Button>
                        </Box>
                        <TableContainer component={Paper}>
                            <Table size="small">
                                <TableHead sx={{ bgcolor: PRIMARY }}>
                                    <TableRow>
                                        {['Name', 'Rule Type', 'Template', 'Schedule', 'Status', 'Allow Resend', 'Actions'].map((h) => (
                                            <TableCell key={h} sx={{ color: '#fff', fontWeight: 600 }}>{h}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rules.map((r) => (
                                        <TableRow key={r._id} hover>
                                            <TableCell>{r.name}</TableCell>
                                            <TableCell>{RULE_TYPE_LABELS[r.rule_type] || r.rule_type}</TableCell>
                                            <TableCell>{r.template_id?.name || '-'}</TableCell>
                                            <TableCell>{formatScheduleLabel(r.schedule)}</TableCell>
                                            <TableCell>{statusChip(r.status)}</TableCell>
                                            <TableCell>{r.allow_resend ? 'Yes' : 'No'}</TableCell>
                                            <TableCell>
                                                <IconButton size="small" onClick={() => { setEditingRule(r); setRuleForm({ name: r.name, rule_type: r.rule_type, template_id: r.template_id?._id || r.template_id, status: r.status, allow_resend: r.allow_resend, conditions_json: r.conditions_json || { passThreshold: 60 }, schedule: { ...defaultSchedule(), ...(r.schedule || {}) } }); setRuleModal(true); }}><EditIcon fontSize="small" /></IconButton>
                                                <IconButton size="small" color="error" onClick={() => handleDeleteRule(r._id)}><DeleteIcon fontSize="small" /></IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <Pagination count={ruleTotalPages} page={rulePage} onChange={(_, p) => setRulePage(p)} sx={{ mt: 2 }} />
                    </Box>
                )}

                {/* History */}
                {tab === 4 && (
                    <Box>
                        <TableContainer component={Paper}>
                            <Table size="small">
                                <TableHead sx={{ bgcolor: PRIMARY }}>
                                    <TableRow>
                                        {['Campaign', 'Subject', 'Recipients', 'Sent', 'Failed', 'Created By', 'Sent Date'].map((h) => (
                                            <TableCell key={h} sx={{ color: '#fff', fontWeight: 600 }}>{h}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {history.map((h) => (
                                        <TableRow key={h._id} hover>
                                            <TableCell>{h.title}</TableCell>
                                            <TableCell>{h.subject}</TableCell>
                                            <TableCell>{h.recipient_count}</TableCell>
                                            <TableCell>{h.sent_count}</TableCell>
                                            <TableCell>{h.failed_count}</TableCell>
                                            <TableCell>{h.created_by ? `${h.created_by.firstName} ${h.created_by.lastName || ''}` : '-'}</TableCell>
                                            <TableCell>{h.sent_at ? new Date(h.sent_at).toLocaleString() : '-'}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <Pagination count={historyTotalPages} page={historyPage} onChange={(_, p) => setHistoryPage(p)} sx={{ mt: 2 }} />
                    </Box>
                )}

                {/* Logs */}
                {tab === 5 && (
                    <Box>
                        <FormControl size="small" sx={{ mb: 2, minWidth: 160 }}>
                            <InputLabel>Status</InputLabel>
                            <Select value={logStatus} label="Status" onChange={(e) => { setLogStatus(e.target.value); setLogPage(1); }}>
                                <MenuItem value="">All</MenuItem>
                                <MenuItem value="queued">Queued</MenuItem>
                                <MenuItem value="sent">Sent</MenuItem>
                                <MenuItem value="failed">Failed</MenuItem>
                            </Select>
                        </FormControl>
                        <TableContainer component={Paper}>
                            <Table size="small">
                                <TableHead sx={{ bgcolor: PRIMARY }}>
                                    <TableRow>
                                        {['Email', 'Subject', 'Student', 'Campaign / Rule', 'Status', 'Sent At', 'Error'].map((h) => (
                                            <TableCell key={h} sx={{ color: '#fff', fontWeight: 600 }}>{h}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {logs.map((l) => (
                                        <TableRow key={l._id} hover>
                                            <TableCell>{l.email}</TableCell>
                                            <TableCell>{l.subject}</TableCell>
                                            <TableCell>{l.student_id ? `${l.student_id.firstName} ${l.student_id.lastName || ''}` : '-'}</TableCell>
                                            <TableCell>{l.campaign_id?.title || l.rule_id?.name || '-'}</TableCell>
                                            <TableCell>{statusChip(l.status)}</TableCell>
                                            <TableCell>{l.sent_at ? new Date(l.sent_at).toLocaleString() : '-'}</TableCell>
                                            <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.error_message || '-'}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <Pagination count={logTotalPages} page={logPage} onChange={(_, p) => setLogPage(p)} sx={{ mt: 2 }} />
                    </Box>
                )}

                {/* Template Modal */}
                <Modal open={templateModal} onClose={() => setTemplateModal(false)}>
                    <Box sx={modalBox}>
                        <Typography variant="h6" mb={2}>{editingTemplate ? 'Edit Template' : 'Create Template'}</Typography>
                        <Grid container spacing={2}>
                            <Grid sx={{ xs: 12, sm: 6 }}>
                                <TextField fullWidth label="Template Name" value={templateForm.name} onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })} />
                            </Grid>
                            <Grid sx={{ xs: 12, sm: 3, width: 150 }}>
                                <FormControl fullWidth>
                                    <InputLabel>Type</InputLabel>
                                    <Select value={templateForm.type} label="Type" onChange={(e) => setTemplateForm({ ...templateForm, type: e.target.value })}>
                                        {['general', 'welcome', 'reminder', 'assessment', 'examination', 'campaign'].map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid sx={{ xs: 12, sm: 3, width: 150 }} >
                                <FormControl fullWidth>
                                    <InputLabel>Status</InputLabel>
                                    <Select value={templateForm.status} label="Status" onChange={(e) => setTemplateForm({ ...templateForm, status: e.target.value })}>
                                        <MenuItem value="active">Active</MenuItem>
                                        <MenuItem value="inactive">Inactive</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid sx={{ xs: 12 }}>
                                <TextField fullWidth label="Subject" value={templateForm.subject} onChange={(e) => setTemplateForm({ ...templateForm, subject: e.target.value })} helperText={`Use placeholders: ${placeholders.map((p) => `{{${p}}}`).join(', ')}`} />
                            </Grid>
                            <Grid sx={{ xs: 12, width: '97%', margin: '0 auto' }}>
                                <RichTextEditor label="Email Body" value={templateForm.body} onChange={(body) => setTemplateForm({ ...templateForm, body })} />
                            </Grid>
                        </Grid>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
                            <Button onClick={() => setTemplateModal(false)}>Cancel</Button>
                            <Button variant="contained" sx={{ bgcolor: PRIMARY }} onClick={handleSaveTemplate}>Save</Button>
                        </Box>
                    </Box>
                </Modal>

                {/* Campaign Modal */}
                <Modal open={campaignModal} onClose={() => { setCampaignModal(false); resetCampaignForm(); }}>
                    <Box sx={modalBox}>
                        {/* {( campaignLoading || previewLoading) && <Progress />} */}
                        
                        <Typography variant="h6" mb={2}>{editingCampaign ? 'Edit Email Campaign' : 'Create Email Campaign'}</Typography>
                        <Grid container spacing={2}>
                            <Grid sx={{ xs: 12, sm: 6 }} >
                                <TextField fullWidth label="Campaign Name" value={campaignForm.title} onChange={(e) => setCampaignForm({ ...campaignForm, title: e.target.value })} />
                            </Grid>
                            <Grid sx={{ xs: 12, sm: 6, width: 200 }}>
                                <FormControl fullWidth>
                                    <InputLabel>Template (optional)</InputLabel>
                                    <Select value={campaignForm.template_id} label="Template (optional)" onChange={(e) => { applyTemplateToCampaign(e.target.value); loadFullTemplate(e.target.value); }}>
                                        <MenuItem value="">None</MenuItem>
                                        {templateDropdown.map((t) => <MenuItem key={t._id} value={t._id}>{t.name}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid sx={{ xs: 12 }}>
                                <TextField fullWidth label="Subject" value={campaignForm.subject} onChange={(e) => setCampaignForm({ ...campaignForm, subject: e.target.value })} />
                            </Grid>
                            <Grid sx={{ xs: 12, width: '97%' }}>
                                <RichTextEditor label="Email Body" value={campaignForm.body} onChange={(body) => setCampaignForm({ ...campaignForm, body })} />
                            </Grid>

                            <Grid sx={{ xs: 12 }}><Typography variant="subtitle2" fontWeight={600}>Recipient Filters</Typography></Grid>
                            <Grid sx={{ xs: 12, sm: 6 }}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Institute</InputLabel>
                                    <Select value={campaignForm.filters_json.institute.mode} label="Institute" onChange={(e) => updateFilter('institute', 'mode', e.target.value)}>
                                        <MenuItem value="all">All Institutes</MenuItem>
                                        <MenuItem value="single">Single Institute</MenuItem>
                                        <MenuItem value="multiple">Multiple Institutes</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            {campaignForm.filters_json.institute.mode !== 'all' && (
                                <Grid sx={{ xs: 12, sm: 6, width: 175 }}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Select Institute(s)</InputLabel>
                                        <Select multiple value={campaignForm.filters_json.institute.ids} input={<OutlinedInput label="Select Institute(s)" />} onChange={(e) => updateFilter('institute', 'ids', e.target.value)} renderValue={(sel) => sel.map((id) => colleges.find((c) => c._id === id)?.collegeName).filter(Boolean).join(', ')}>
                                            {colleges.map((c) => (
                                                <MenuItem key={c._id} value={c._id}><Checkbox checked={campaignForm.filters_json.institute.ids.includes(c._id)} /><ListItemText primary={c.collegeName} /></MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            )}
                            <Grid sx={{ xs: 12, sm: 6 }}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Course</InputLabel>
                                    <Select value={campaignForm.filters_json.course.mode} label="Course" onChange={(e) => updateFilter('course', 'mode', e.target.value)}>
                                        <MenuItem value="all">All Courses</MenuItem>
                                        <MenuItem value="single">Single Course</MenuItem>
                                        <MenuItem value="multiple">Multiple Courses</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            {campaignForm.filters_json.course.mode !== 'all' && (
                                <Grid sx={{ xs: 12, sm: 6, width: 170 }}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Select Course(s)</InputLabel>
                                        <Select multiple value={campaignForm.filters_json.course.ids} input={<OutlinedInput label="Select Course(s)" />} onChange={(e) => updateFilter('course', 'ids', e.target.value)} renderValue={(sel) => sel.map((id) => courses.find((c) => c._id === id)?.courseName).filter(Boolean).join(', ')}>
                                            {courses.map((c) => (
                                                <MenuItem key={c._id} value={c._id}><Checkbox checked={campaignForm.filters_json.course.ids.includes(c._id)} /><ListItemText primary={c.courseName} /></MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            )}
                            <Grid sx={{ xs: 12, sm: 4, width: 150 }}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Progress</InputLabel>
                                    <Select value={campaignForm.filters_json.progress.type} label="Progress" onChange={(e) => updateFilter('progress', 'type', e.target.value)}>
                                        <MenuItem value="">Any</MenuItem>
                                        <MenuItem value="not_started">Not Started (0%)</MenuItem>
                                        <MenuItem value="less_than">Less Than X%</MenuItem>
                                        <MenuItem value="greater_than">Greater Than X%</MenuItem>
                                        <MenuItem value="completed">Completed</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            {['less_than', 'greater_than'].includes(campaignForm.filters_json.progress.type) && (
                                <Grid sx={{ xs: 12, sm: 2 }}>
                                    <TextField fullWidth size="small" type="number" label="X %" value={campaignForm.filters_json.progress.value} onChange={(e) => updateFilter('progress', 'value', e.target.value)} />
                                </Grid>
                            )}
                            <Grid sx={{ xs: 12, sm: 4 }}>
                                <TextField fullWidth size="small" type="number" label="Inactive for X Days" value={campaignForm.filters_json.activity.inactiveDays} onChange={(e) => updateFilter('activity', 'inactiveDays', e.target.value)} />
                            </Grid>
                            <Grid sx={{ xs: 12 }}>
                                {/* <Button variant="outlined" onClick={previewRecipients}>Preview Recipient Count</Button>
                                {recipientPreview != null && <Chip label={`${recipientPreview} recipients`} color="primary" sx={{ ml: 2 }} />} */}
                                <Button variant="outlined" onClick={previewRecipients} disabled={previewLoading} >
                                    {previewLoading ? "Checking..." : "Preview Recipient Count"}
                                </Button>
                                {recipientPreview != null && <Chip label={`${recipientPreview} recipients`} color="primary" sx={{ ml: 2 }} />}
                            </Grid>

                            <Grid sx={{ xs: 12, sm: 6, width: 150 }}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Send Mode</InputLabel>
                                    <Select value={campaignForm.send_mode} label="Send Mode" onChange={(e) => setCampaignForm({ ...campaignForm, send_mode: e.target.value })}>
                                        {!editingCampaign && <MenuItem value="now">Send Now</MenuItem>}
                                        <MenuItem value="schedule">Schedule Later</MenuItem>
                                        <MenuItem value="draft">Save as Draft</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            {campaignForm.send_mode === 'schedule' && (
                                <Grid sx={{ xs: 12, sm: 6 }}>
                                    <TextField fullWidth size="small" type="datetime-local" label="Scheduled Date" InputLabelProps={{ shrink: true }} value={campaignForm.scheduled_at} onChange={(e) => setCampaignForm({ ...campaignForm, send_mode: 'schedule', scheduled_at: e.target.value })} />
                                </Grid>
                            )}

                            {( campaignLoading || previewLoading) && <Progress />}
                        </Grid>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
                            <Button onClick={() => { setCampaignModal(false); resetCampaignForm(); }}>Cancel</Button>
                            {/* <Button variant="contained" sx={{ bgcolor: PRIMARY }} onClick={handleSaveCampaign}>
                                {editingCampaign
                                    ? (campaignForm.send_mode === 'schedule' ? 'Update Schedule' : 'Save Changes')
                                    : (campaignForm.send_mode === 'now' ? 'Create & Send' : campaignForm.send_mode === 'schedule' ? 'Schedule Campaign' : 'Save Draft')}
                            </Button> */}
                            <Button variant="contained" sx={{ bgcolor: PRIMARY }} onClick={handleSaveCampaign} disabled={campaignLoading} >
                                {campaignLoading
                                    ? "Please wait..."
                                    : editingCampaign
                                        ? (campaignForm.send_mode === "schedule"
                                            ? "Update Schedule"
                                            : "Save Changes")
                                        : (campaignForm.send_mode === "now"
                                            ? "Create & Send"
                                            : campaignForm.send_mode === "schedule"
                                                ? "Schedule Campaign"
                                                : "Save Draft")}
                            </Button>
                        </Box>
                        {/* <Backdrop
                            open={campaignLoading || previewLoading}
                            sx={{
                                color: "#fff",
                                zIndex: (theme) => theme.zIndex.modal + 1,
                            }}
                        >
                            <Progress />
                        </Backdrop> */}
                    </Box>
                </Modal>

                {/* Rule Modal */}
                <Modal open={ruleModal} onClose={() => setRuleModal(false)}>
                    <Box sx={{ ...modalBox, width: { xs: '95vw', sm: 600 } }}>
                        <Typography variant="h6" mb={2}>{editingRule ? 'Edit Rule' : 'Create Automated Rule'}</Typography>
                        <Grid container spacing={2}>
                            <Grid sx={{ xs: 12 }}>
                                <TextField fullWidth label="Rule Name" value={ruleForm.name} onChange={(e) => setRuleForm({ ...ruleForm, name: e.target.value })} />
                            </Grid>
                            <Grid sx={{ xs: 12, width: 115 }}>
                                <FormControl fullWidth>
                                    <InputLabel>Rule Type</InputLabel>
                                    <Select value={ruleForm.rule_type} label="Rule Type" onChange={(e) => setRuleForm({ ...ruleForm, rule_type: e.target.value })}>
                                        {ruleTypes.map((rt) => <MenuItem key={rt} value={rt}>{RULE_TYPE_LABELS[rt] || rt}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid sx={{ xs: 12, width: 155 }}>
                                <FormControl fullWidth>
                                    <InputLabel>Email Template</InputLabel>
                                    <Select value={ruleForm.template_id} label="Email Template" onChange={(e) => setRuleForm({ ...ruleForm, template_id: e.target.value })}>
                                        {templateDropdown.map((t) => <MenuItem key={t._id} value={t._id}>{t.name}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid>
                            {['assessment_failed', 'assessment_passed'].includes(ruleForm.rule_type) && (
                                <Grid sx={{ xs: 12 }}>
                                    <TextField fullWidth type="number" label="Pass Threshold (%)" value={ruleForm.conditions_json?.passThreshold ?? 60} onChange={(e) => setRuleForm({ ...ruleForm, conditions_json: { ...ruleForm.conditions_json, passThreshold: Number(e.target.value) } })} />
                                </Grid>
                            )}
                            <Grid sx={{ xs: 12 }}>
                                <Typography variant="subtitle2" fontWeight={600}>Run Schedule</Typography>
                            </Grid>
                            <Grid sx={{ xs: 12, sm: 6 }}>
                                <FormControl fullWidth>
                                    <InputLabel>Frequency</InputLabel>
                                    <Select
                                        value={ruleForm.schedule?.frequency || 'daily'}
                                        label="Frequency"
                                        onChange={(e) => setRuleForm({ ...ruleForm, schedule: { ...ruleForm.schedule, frequency: e.target.value } })}
                                    >
                                        <MenuItem value="daily">Daily</MenuItem>
                                        <MenuItem value="weekly">Weekly</MenuItem>
                                        <MenuItem value="monthly">Monthly</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            {ruleForm.schedule?.frequency === 'weekly' && (
                                <Grid sx={{ xs: 12, sm: 6 }}>
                                    <FormControl fullWidth>
                                        <InputLabel>Day of Week</InputLabel>
                                        <Select
                                            value={ruleForm.schedule?.day_of_week ?? 1}
                                            label="Day of Week"
                                            onChange={(e) => setRuleForm({ ...ruleForm, schedule: { ...ruleForm.schedule, day_of_week: Number(e.target.value) } })}
                                        >
                                            {(scheduleOptions.days_of_week || []).map((d) => (
                                                <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            )}
                            {ruleForm.schedule?.frequency === 'monthly' && (
                                <Grid sx={{ xs: 12, sm: 6 }}>
                                    <FormControl fullWidth>
                                        <InputLabel>Day of Month</InputLabel>
                                        <Select
                                            value={ruleForm.schedule?.day_of_month ?? 1}
                                            label="Day of Month"
                                            onChange={(e) => setRuleForm({ ...ruleForm, schedule: { ...ruleForm.schedule, day_of_month: Number(e.target.value) } })}
                                        >
                                            {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                                                <MenuItem key={day} value={day}>{day}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            )}
                            <Grid sx={{ xs: 6, sm: 3 }}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label="Hour (0–23)"
                                    inputProps={{ min: 0, max: 23 }}
                                    value={ruleForm.schedule?.hour ?? 9}
                                    onChange={(e) => setRuleForm({ ...ruleForm, schedule: { ...ruleForm.schedule, hour: Number(e.target.value) } })}
                                />
                            </Grid>
                            <Grid sx={{ xs: 6, sm: 3 }}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label="Minute (0–59)"
                                    inputProps={{ min: 0, max: 59 }}
                                    value={ruleForm.schedule?.minute ?? 0}
                                    onChange={(e) => setRuleForm({ ...ruleForm, schedule: { ...ruleForm.schedule, minute: Number(e.target.value) } })}
                                />
                            </Grid>
                            <Grid sx={{ xs: 12 }}>
                                <Alert severity="info" sx={{ py: 0.5 }}>
                                    {formatScheduleLabel(ruleForm.schedule)}. Students who have completed the course are excluded automatically.
                                </Alert>
                            </Grid>
                            <Grid sx={{ xs: 12 }}>
                                <FormControl fullWidth>
                                    <InputLabel>Status</InputLabel>
                                    <Select value={ruleForm.status} label="Status" onChange={(e) => setRuleForm({ ...ruleForm, status: e.target.value })}>
                                        <MenuItem value="active">Active</MenuItem>
                                        <MenuItem value="inactive">Inactive</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid sx={{ xs: 12 }}>
                                <FormControlLabel control={<Switch checked={ruleForm.allow_resend} onChange={(e) => setRuleForm({ ...ruleForm, allow_resend: e.target.checked })} />} label="Allow resend (disable duplicate prevention)" />
                            </Grid>
                        </Grid>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
                            <Button onClick={() => setRuleModal(false)}>Cancel</Button>
                            <Button variant="contained" sx={{ bgcolor: PRIMARY }} onClick={handleSaveRule}>Save</Button>
                        </Box>
                    </Box>
                </Modal>
            </Box>
        </Layout>
    );
};

export default NotificationCenter;
